// src/screens/s2/WhatS2Changed.tsx
// ─────────────────────────────────────────────────────────────────────────────
// Session 2 Screen 13 — What S2 Changed.
// Gap-movement waterfall showing this session's contribution only:
//   Gap entering S2 → Pension lever → CT saving reinvested → Profit driver
//   target (dotted, projected) → Gap remaining.
// Before/after freedom age card pair below with years-gained callout.
// ─────────────────────────────────────────────────────────────────────────────

import React from 'react';
import {
  ComposedChart, Bar, Line, Cell, XAxis, YAxis, Tooltip, CartesianGrid,
  ResponsiveContainer, ReferenceLine,
} from 'recharts';
import { ClientData } from '../../types/ClientData';
import { effectiveCTRate } from '../../taxConfig';
import { fvContributions } from '../../utils/planCalculations';
import { formatCurrency, formatAge } from '../../utils/formatters';

interface Props {
  data: ClientData;
  onUpdate: (updates: Partial<ClientData>) => void;
}

export default function WhatS2Changed({ data }: Props) {
  // ── Starting gap entering S2 ───────────────────────────────────────────
  const gapEntering = data.s2GapBefore ?? data.gapAmount ?? 0;

  // ── Pension lever — confirmed monthly commitment ───────────────────────
  const clientAge = data.clientAge || 40;
  const freedomAge = data.targetFreedomAge || 55;
  const yearsToFreedom = Math.max(0, freedomAge - clientAge);
  const growthRate = data.assumptionPensionGrowth || 0.08;
  const s2PensionMonthly = data.s2PensionMonthly ?? 0;
  const s2PensionPot = fvContributions(s2PensionMonthly, growthRate * 100, yearsToFreedom);
  const pensionAnnualIncomeImpact = s2PensionPot * 0.04;

  // ── CT saving reinvested — pension × effective CT rate, compounded ────
  const profit = data.estimatedCompanyProfit || 0;
  const ctRate = effectiveCTRate(profit);
  const ctAnnualSaved = s2PensionMonthly * 12 * ctRate;
  const ctReinvestPot = fvContributions(ctAnnualSaved / 12, growthRate * 100, yearsToFreedom);
  const ctReinvestAnnualIncomeImpact = ctReinvestPot * 0.04;

  // ── Profit driver target (dotted — projected, not confirmed) ──────────
  // No ClientData field tracks a committed lever target, so the dotted bar
  // uses the same pension headroom × 30% rule of thumb as a placeholder. If
  // left at zero the bar still renders (labelled "not committed").
  const profitDriverProjected = s2PensionMonthly * 12 * 0.3;

  // ── Remaining gap ──────────────────────────────────────────────────────
  const gapAfter = Math.max(
    0,
    gapEntering - pensionAnnualIncomeImpact - ctReinvestAnnualIncomeImpact - profitDriverProjected
  );

  // ── Waterfall data (stacked-spacer) ───────────────────────────────────
  // Anchors (entering / remaining gap) sit at floor=0; deductions float
  // between running-before and running-after. `isDotted` flag keyed to the
  // projection bar so the cell render uses a lighter fill + dashed stroke.
  type WFRow = { name: string; spacer: number; value: number; running: number; fill: string; isDotted: boolean };
  const waterfallData: WFRow[] = [];
  let wfRun = 0;
  const wfAnchor = (name: string, fill: string) => {
    waterfallData.push({ name, spacer: 0, value: wfRun, running: wfRun, fill, isDotted: false });
  };
  const wfDeduct = (name: string, amount: number, fill: string, isDotted = false) => {
    const before = wfRun;
    const after = before - amount;
    waterfallData.push({
      name,
      spacer: Math.min(before, after),
      value: Math.abs(before - after),
      running: after,
      fill,
      isDotted,
    });
    wfRun = after;
  };
  wfRun = gapEntering;
  wfAnchor('Gap entering S2', '#94A3B8');
  wfDeduct('− Pension lever', pensionAnnualIncomeImpact, '#10B981');
  wfDeduct('− CT reinvested', ctReinvestAnnualIncomeImpact, '#C9A84C');
  wfDeduct('− Profit driver', profitDriverProjected, '#60A5FA', true);
  wfAnchor('Gap remaining', '#1E3A5F');

  // ── Freedom age impact ────────────────────────────────────────────────
  const ageEntering = data.targetFreedomAge ?? 0;
  const ageAfter = data.s2FreedomAgeAfter ?? ageEntering;
  const yearsGained = Math.max(0, ageEntering - ageAfter);

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-ma-navy">What S2 Changed</h2>
        <p className="text-gray-500 mt-1 text-sm">
          The gap movement this session — confirmed levers in solid colour,
          projected targets dotted.
        </p>
      </div>

      {/* WATERFALL */}
      <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6">
        <div style={{ width: '100%', height: 320 }}>
          <ResponsiveContainer>
            <ComposedChart data={waterfallData} margin={{ top: 20, right: 20, bottom: 8, left: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} stroke="#9CA3AF" />
              <YAxis tick={{ fontSize: 10 }} stroke="#9CA3AF" tickFormatter={(v) => `£${Math.round(v / 1000)}k`} />
              <Tooltip formatter={(v: any) => formatCurrency(Number(v))} />
              <ReferenceLine y={0} stroke="#9CA3AF" />
              {/* Stacked-spacer waterfall: transparent spacer + coloured value. */}
              <Bar dataKey="spacer" stackId="gap" fill="transparent" />
              <Bar dataKey="value" stackId="gap" fill="#1E3A5F" radius={[4, 4, 0, 0]}>
                {waterfallData.map((entry, i) => (
                  <Cell
                    key={i}
                    fill={entry.isDotted ? `${entry.fill}40` : entry.fill}
                    stroke={entry.isDotted ? entry.fill : undefined}
                    strokeWidth={entry.isDotted ? 2 : 0}
                    strokeDasharray={entry.isDotted ? '4 2' : undefined}
                  />
                ))}
              </Bar>
              <Line
                type="monotone"
                dataKey="running"
                stroke="#94A3B8"
                strokeWidth={2}
                strokeDasharray="4 2"
                dot={{ r: 3, fill: '#94A3B8' }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
        <p className="text-xs text-gray-500 mt-3">
          Solid bars = pension commitment and the CT saving it triggers (confirmed
          this session). Dotted blue bar = profit-driver lever target (projected;
          commit on the Business Action Plan to lock in).
        </p>
      </div>

      {/* FREEDOM AGE CARD PAIR */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="rounded-2xl border border-gray-200 bg-gray-50 p-6 text-center">
          <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1">Entering S2</div>
          <div className="text-4xl font-bold text-gray-600">Age {ageEntering > 0 ? formatAge(ageEntering) : '—'}</div>
          <p className="text-xs text-gray-500 mt-2">Position going into today's session.</p>
        </div>
        <div className="rounded-2xl border-2 p-6 text-center" style={{ borderColor: '#C9A84C', backgroundColor: '#FFF9ED' }}>
          <div className="text-xs font-semibold uppercase tracking-wide mb-1" style={{ color: '#8B7A3E' }}>After S2</div>
          <div className="text-4xl font-bold" style={{ color: '#C9A84C' }}>Age {ageAfter > 0 ? formatAge(ageAfter) : '—'}</div>
          <p className="text-xs mt-2" style={{ color: '#8B7A3E' }}>
            {yearsGained > 0
              ? `${yearsGained} year${yearsGained === 1 ? '' : 's'} gained this session.`
              : 'No age movement recorded — confirm the pension lever on Screen 8.'}
          </p>
        </div>
      </div>

      {/* YEARS GAINED HEADLINE */}
      {yearsGained > 0 && (
        <div className="rounded-2xl p-6 text-center text-white" style={{ background: 'linear-gradient(135deg, #1B2B4B 0%, #2a3f6b 100%)' }}>
          <p className="text-sm uppercase tracking-wide opacity-80 mb-2">S2 outcome</p>
          <p className="text-3xl font-bold" style={{ color: '#C9A84C' }}>
            {yearsGained} year{yearsGained === 1 ? '' : 's'} gained.
          </p>
          <p className="text-sm opacity-80 mt-2">
            Pension commitment of {formatCurrency(s2PensionMonthly)}/mo plus {formatCurrency(ctAnnualSaved)}/yr of CT relief.
          </p>
        </div>
      )}
    </div>
  );
}
